<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Brand logo -->
      <b-link :to="{ name: 'home' }" class="brand-logo">
        <b-img
          src="/images/CG-Logo-S.png"
          alt="Company Logo"
          style="max-width: auto !important"
        />
      </b-link>

      <!-- Left Image -->
      <b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img fluid :src="imgUrl" alt="Login Illustration" />
        </div>
      </b-col>

      <!-- Login Form -->
      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <b-card-title title-tag="h2" class="font-weight-bold mb-1">
            Welcome to MiME Self-Care!
          </b-card-title>

          <!-- Form -->
          <validation-observer ref="loginValidation" v-slot="{ invalid }">
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="validationForm"
            >
              <!-- Username Field -->
              <b-form-group label="Username" label-for="login-username">
                <validation-provider
                  #default="{ errors }"
                  name="Username"
                  rules="required"
                >
                  <b-form-input
                    id="login-username"
                    v-model.trim="form.username"
                    :state="errors.length > 0 ? false : null"
                    name="login-username"
                    placeholder="Enter your username"
                    autocomplete="username"
                    :disabled="isLoading"
                    @keyup.enter="!invalid && validationForm()"
                  />
                  <small v-if="errors.length" class="text-danger d-block mt-1">
                    {{ errors[0] }}
                  </small>
                </validation-provider>
              </b-form-group>

              <!-- Password Field -->
              <b-form-group label="Password" label-for="login-password">
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <b-form-input
                      id="login-password"
                      v-model.trim="form.password"
                      :state="errors.length > 0 ? false : null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="Enter your password"
                      autocomplete="current-password"
                      :disabled="isLoading"
                      @keyup.enter="!invalid && validationForm()"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small v-if="errors.length" class="text-danger d-block mt-1">
                    {{ errors[0] }}
                  </small>
                </validation-provider>
              </b-form-group>

              <!-- Submit Button -->
              <b-button type="submit" variant="primary" block class="mt-3">
                <template v-if="isLoading">
                  <b-spinner small class="mr-1" />
                  Signing in...
                </template>
                <template v-else> Sign in </template>
              </b-button>
            </b-form>
          </validation-observer>

          <!-- Help Text -->
          <!-- Help Text -->
          <b-card-text class="mt-2">
            <p
              class="text-warning mb-0 cursor-pointer"
              @click="showForgotModal"
            >
              <strong>Forgot your password?</strong>
            </p>
          </b-card-text>
        </b-col>
      </b-col>
    </b-row>

    <b-modal
      id="forgot-password-modal"
      title="Forgot your password?"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <validation-observer ref="forgotValidation" v-slot="{ invalid }">
        <b-form @submit.prevent="submitForgotPassword">
          <b-form-group label="Email Address">
            <validation-provider
              #default="{ errors }"
              name="Email"
              rules="required|email"
            >
              <b-form-input
                v-model="forgotEmail"
                type="email"
                placeholder="Enter your email address"
                @keyup.enter="!invalid && validationForm()"
                required
              />
              <small v-if="errors.length" class="text-danger d-block mt-1">
                {{ errors[0] }}
              </small>
            </validation-provider>
          </b-form-group>

          <div class="text-center mt-2">
            <b-button type="submit" variant="primary" :disabled="forgotLoading">
              <b-spinner small v-if="forgotLoading" class="mr-1" />
              Submit
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import store from "@/store/index";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import api from "@/services/api";
import { handleApiError } from "@/utils/handleError";

import { encrypt } from "@/utils/crypto";

// Import Bootstrap Vue components directly (better for production)
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BSpinner,
} from "bootstrap-vue";

// Lazy load images
const getSideImage = (skin) => {
  return skin === "dark"
    ? require("@/assets/images/pages/login-v2-dark.svg")
    : require("@/assets/images/pages/login-v2.svg");
};

export default {
  name: "Login",
  components: {
    ValidationProvider,
    ValidationObserver,
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BSpinner,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      form: {
        username: "",
        password: "",
        direct_login_by_user: "",
      },
      isLoading: false,
      forgotLoading: false,
      required,
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    imgUrl() {
      return getSideImage(store.state.appConfig.layout.skin);
    },
  },
  mounted() {
    // Check for direct login parameters
    const { username, direct_login_by_user } = this.$route.query;
    if (username) {
      this.form.username = username;
    }
    if (direct_login_by_user) {
      this.form.direct_login_by_user = direct_login_by_user;
    }

    // Auto-focus username field
    this.$nextTick(() => {
      const usernameInput = document.getElementById("login-username");
      if (usernameInput) usernameInput.focus();
    });
  },
  methods: {
    /**
     * Validate and submit login form
     */
    async validationForm() {
      // Prevent double submission
      if (this.isLoading) return;

      // Validate form
      const isValid = await this.$refs.loginValidation.validate();
      if (!isValid) return;

      // Start loading
      this.isLoading = true;
      const loader = this.$showCustomLoader();

      try {
        // Prepare payload
        const payload = {
          username: this.form.username,
          password: this.form.password,
        };

        // Add direct login parameter if present
        if (this.form.direct_login_by_user) {
          payload.direct_login_by_user = this.form.direct_login_by_user;
        }

        // Login API call
        const { data } = await api.post("/selfcare/api/v1/login/", payload);

        // Handle successful login
        await this.handleLoginSuccess(data);
      } catch (error) {
        // Handle login error
        await this.handleLoginError(error);
      } finally {
        // Cleanup
        this.isLoading = false;
        if (loader) loader.hide();
      }
    },

    /**
     * Handle successful login response
     */
    async handleLoginSuccess(data) {
      const {
        token,
        access_lifetime,
        u_id,
        profile_id,
        username,
        fullname,
        u_type,
        refresh_token,
      } = data;

      // Store auth data
      localStorage.setItem("accessToken", token);
      localStorage.setItem("accessLifetime", access_lifetime);

      localStorage.setItem(
        "userData",
        encrypt({ u_id, profile_id, username, fullname, u_type }),
      );

      // Update Vuex store
      this.$store.commit("app/AUTH_SUCCESS", token);

      // Show success message
      this.$toast({
        component: ToastificationContent,
        props: {
          title: `Welcome ${fullname || username}`,
          text: `You have logged in as ${username}`,
          icon: "CoffeeIcon",
          variant: "success",
        },
      });

      // Fetch additional user details (non-blocking)
      this.fetchUserDetails(username);

      // Load menus and redirect
      await this.loadMenusAndRedirect();
    },

    /**
     * Fetch additional user details
     */
    async fetchUserDetails(username) {
      try {
        const response = await api.get(
          `/selfcare/api/v1/customer/${username}/`,
        );

        if (response?.data) {
          const {
            salutation,
            first_name,
            last_name,
            company,
            od_balance,
            interested_on,
            detail_contact_address,
          } = response.data;

          localStorage.setItem(
            "userDetails",
            JSON.stringify({
              salutation,
              first_name,
              last_name,
              company,
              od_balance,
              interested_on,
              ...detail_contact_address,
            }),
          );
        }
      } catch (error) {
        console.warn("Failed to fetch user details:", error);
        // Non-critical error, continue with login
      }
    },

    /**
     * Load menus and redirect to dashboard
     */
    async loadMenusAndRedirect() {
      const menuLoader = this.$showCustomLoader();
      this.$router.push("/admin-dashboard");
      menuLoader.hide();
    },

    /**
     * Handle login errors
     */
    async handleLoginError(error) {
      console.error("Login error:", error);

      // Preserve query parameters in URL
      this.$router
        .replace({
          path: this.$route.path,
          query: {
            username: this.form.username,
            ...(this.form.direct_login_by_user && {
              direct_login_by_user: this.form.direct_login_by_user,
            }),
          },
        })
        .catch(() => {
          // Ignore navigation duplicates
        });

      // Show error message
      const message = handleApiError(
        error,
        "Unauthorized access. Please check your credentials.",
      );

      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Login Failed",
          text: message,
          icon: "AlertCircleIcon",
          variant: "danger",
        },
      });

      // Clear password field
      this.form.password = "";

      // Focus password field for retry
      this.$nextTick(() => {
        const passwordInput = document.getElementById("login-password");
        if (passwordInput) passwordInput.focus();
      });
    },

    showForgotModal() {
      this.forgotEmail = "";
      this.$bvModal.show("forgot-password-modal");
    },

    async submitForgotPassword() {
      const isValid = await this.$refs.forgotValidation.validate();
      if (!isValid) return;

      this.forgotLoading = true;
      const loader = this.$showCustomLoader();

      try {
        const payload = {
          email: this.forgotEmail,
        };

        const { data } = await api.post(
          "/selfcare/api/v1/forget-password/",
          payload,
        );

        // API message check
        if (data.message && data.message.toLowerCase().includes("not found")) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: data.message,
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          });

          return;
        }

        // Success case
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            text: "Password reset link has been sent to your email. Please check your email to reset your password.",
            icon: "MailIcon",
            variant: "success",
          },
        });

        this.$bvModal.hide("forgot-password-modal");
      } catch (error) {
        const message = handleApiError(
          error,
          "Failed to send password reset link.",
        );

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: message,
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        loader.hide();
        this.forgotLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.brand-logo img {
  max-width: auto !important;
}

.auth-login-form {
  .form-control {
    &::placeholder {
      color: #b9b9c3;
    }

    &:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
    }
  }

  .input-group-merge.is-invalid {
    .form-control {
      border-color: #ea5455;
    }
  }
}

.cursor-pointer {
  cursor: pointer;
}

.text-warning {
  color: #ff9f43 !important;
}

.text-info {
  color: #00cfe8 !important;
}

.btn-primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
